import { useJoinCall } from '@doneverse/data/hooks'
import { useCallback } from 'react'
import { hasNativeCalls, postNativeCallStart } from '@/lib/mobile-bridge'
import { useDashboardStore } from '@/store'

/**
 * Start a call in a conversation, or join the one already running.
 *
 * One call for both cases — the API has a single start-or-join endpoint (Slack
 * huddles have no separate "start", and neither do we), and the response tells
 * us which happened. Shared by the chat header button, the incoming-call
 * popup/banner, and the in-thread call card, so all behave identically —
 * including the guard below.
 *
 * `blocked` is true while the user is in a call in ANOTHER conversation.
 * There is exactly one media surface (see CallHost), so joining here would
 * silently overwrite the active call without hanging up its media connection
 * or telling the server we left. The user must leave first; every affordance
 * that calls `join` disables itself on `blocked`.
 */
export function useStartOrJoinCall(contactId: string, contactName: string) {
  const setActiveCall = useDashboardStore((s) => s.setActiveCall)
  const removeIncomingCall = useDashboardStore((s) => s.removeIncomingCall)
  const activeCall = useDashboardStore((s) => s.activeCall)

  const blocked = activeCall !== null && activeCall.contactId !== contactId

  const joinCall = useJoinCall({
    onSuccess: (result) => {
      setActiveCall({
        callId: result.call.id,
        contactId,
        contactName,
        meetingId: result.meeting_id,
        authToken: result.auth_token,
        participantId: result.participant_id,
        minimized: false,
        ring: result.call.ring ?? [],
        releasing: null,
        joinedAt: Date.now(),
      })
      // We're in it now, so the "Join" pill for this conversation is redundant.
      removeIncomingCall(contactId)
    },
  })

  const join = useCallback(() => {
    if (joinCall.isPending || blocked) return
    if (hasNativeCalls() && postNativeCallStart(contactId, contactName)) return
    joinCall.mutate(contactId)
  }, [contactId, contactName, joinCall, blocked])

  return { join, isJoining: joinCall.isPending, blocked }
}
